import { Link, router, usePage } from '@inertiajs/react'
import { useState } from 'react'

const STATUS_COLORS = {
  published: 'bg-green-100 text-green-800',
  draft:     'bg-yellow-100 text-yellow-800',
  archived:  'bg-gray-100 text-gray-500',
}

const CONTEXT_ICONS = {
  coffee_shop:    '☕',
  food_service:   '🍽',
  expo_booth:     '🏪',
  estate_sale:    '🏠',
  yard_sale:      '🌿',
  pop_up_retail:  '🎪',
  trading_cards:  '🃏',
  event_vendor:   '🎟',
  mobile_service: '📱',
  general:        '🛒',
}

export default function Menus() {
  const { menus, total, page, per_page, filters } = usePage().props
  const [q, setQ] = useState(filters.q ?? '')

  function buildUrl(overrides = {}) {
    const p = new URLSearchParams()
    const merged = { ...filters, page: 1, ...overrides }
    Object.entries(merged).forEach(([k, v]) => { if (v) p.set(k, v) })
    return `/admin/menus?${p.toString()}`
  }

  function handleSearch(e) {
    e.preventDefault()
    router.visit(buildUrl({ q }))
  }

  const totalPages = Math.ceil(total / per_page)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/users" className="text-gray-500 hover:text-gray-700 text-sm">← Users</Link>
            <Link href="/admin/queue_monitor" className="text-gray-500 hover:text-gray-700 text-sm">Queue Monitor</Link>
          </div>
          <h1 className="text-xl font-bold text-gray-900">Menu Catalog</h1>
          <span className="text-sm text-gray-500">{total} menu{total !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex flex-wrap items-center gap-3">
        {/* Status tabs */}
        <div className="flex gap-1">
          {['', 'published', 'draft', 'archived'].map(s => (
            <Link
              key={s}
              href={buildUrl({ status: s, page: 1 })}
              className={`px-3 py-1 rounded text-sm font-medium ${
                (filters.status ?? '') === s
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </Link>
          ))}
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 ml-auto">
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search name or description…"
            className="border border-gray-300 rounded px-3 py-1 text-sm w-64"
          />
          <button type="submit" className="bg-gray-700 text-white px-3 py-1 rounded text-sm hover:bg-gray-800">
            Search
          </button>
          {filters.q && (
            <Link href={buildUrl({ q: '', page: 1 })} className="text-sm text-gray-500 hover:text-gray-700 py-1">
              Clear
            </Link>
          )}
        </form>
      </div>

      {/* Table */}
      <div className="px-6 py-4">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Menu</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Creator</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {menus.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">No menus found</td>
                </tr>
              )}
              {menus.map(menu => (
                <tr key={menu.id} className="hover:bg-gray-50">
                  {/* Menu name + context */}
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      <span className="text-xl leading-none mt-0.5">
                        {CONTEXT_ICONS[menu.context_type] ?? '🛒'}
                      </span>
                      <div>
                        <div className="font-medium text-gray-900">{menu.name}</div>
                        {menu.description && (
                          <div className="text-xs text-gray-500 mt-0.5 max-w-xs truncate">{menu.description}</div>
                        )}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {menu.context_type} · {menu.currency?.toUpperCase()} · ID {menu.id}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Creator */}
                  <td className="px-4 py-3">
                    {menu.creator ? (
                      <div>
                        <Link
                          href={`/admin/users?q=${menu.creator.username}`}
                          className="font-medium text-indigo-600 hover:text-indigo-800"
                        >
                          {menu.creator.username}
                        </Link>
                        <div className="text-xs text-gray-400">{menu.creator.email}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>

                  {/* Organization */}
                  <td className="px-4 py-3">
                    <div className="text-xs space-y-0.5">
                      {menu.brand ? (
                        <div><span className="text-gray-400">Brand:</span> <span className="text-gray-700">{menu.brand.name}</span></div>
                      ) : null}
                      {menu.venue ? (
                        <div><span className="text-gray-400">Venue:</span> <span className="text-gray-700">{menu.venue.name}</span></div>
                      ) : null}
                      {!menu.brand && !menu.venue && (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </td>

                  {/* Item count */}
                  <td className="px-4 py-3 text-center">
                    <span className="font-medium text-gray-700">{menu.item_count}</span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[menu.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {menu.status}
                    </span>
                  </td>

                  {/* Updated */}
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {new Date(menu.published_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            {page > 1 && (
              <Link href={buildUrl({ page: page - 1 })} className="px-3 py-1 border rounded text-sm hover:bg-gray-100">← Prev</Link>
            )}
            <span className="px-3 py-1 text-sm text-gray-600">Page {page} of {totalPages}</span>
            {page < totalPages && (
              <Link href={buildUrl({ page: page + 1 })} className="px-3 py-1 border rounded text-sm hover:bg-gray-100">Next →</Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
